import { Component, Input, OnInit } from '@angular/core';
import { AiAssistService, InventoryInsights } from '../ai-assist.service';

/**
 * AI Store / Inventory insights dashboard.
 * Runs on a live stock list passed via [items], else a built-in sample so it
 * always demos. DeepSeek returns expiry-risk / reorder / dead-stock actions.
 */
@Component({
  selector: 'app-ai-store-insights',
  templateUrl: './ai-store-insights.component.html',
  styleUrls: ['./ai-store-insights.component.scss'],
})
export class AiStoreInsightsComponent implements OnInit {
  /** Live stock feed. When empty, the sample dataset is used. */
  @Input() items: any[] | null = null;
  @Input() asOfDate = '2026-06-02';

  loading = false;
  errored = false;
  usingSample = false;
  data: InventoryInsights | null = null;

  branches: { id: number; name: string }[] = [];
  selectedBranchId: number | null = null;   // null = All branches

  // animated counters
  cItems = 0; cExpiry = 0; cReorder = 0; cDead = 0; cValue = 0;
  // hero "impact" counters
  cImpact = 0; cWriteOff = 0; cFree = 0; cProtected = 0;
  confetti: { left: number; delay: number; bg: string; rot: number }[] = [];

  readonly sample = [
    { name: 'Moxifloxacin 0.5% eye drops', category: 'Pharmacy', batchNo: 'MX231', expDate: '2026-07-10', stockQty: 18, reorderLevel: 25, avgDailyUse: 3, purchasePrice: 85, salesPrice: 120, lastSoldDaysAgo: 1 },
    { name: 'Latanoprost eye drops', category: 'Pharmacy', batchNo: 'LT88', expDate: '2026-06-20', stockQty: 40, reorderLevel: 15, avgDailyUse: 2, purchasePrice: 150, salesPrice: 210, lastSoldDaysAgo: 2 },
    { name: 'Timolol 0.5% eye drops', category: 'Pharmacy', batchNo: 'TM44', expDate: '2026-08-05', stockQty: 9, reorderLevel: 20, avgDailyUse: 2.5, purchasePrice: 60, salesPrice: 95, lastSoldDaysAgo: 1 },
    { name: 'Carboxymethylcellulose lubricant', category: 'Pharmacy', batchNo: 'CM12', expDate: '2026-09-01', stockQty: 6, reorderLevel: 20, avgDailyUse: 4, purchasePrice: 40, salesPrice: 75, lastSoldDaysAgo: 1 },
    { name: 'Designer Titanium Frame TX-9', category: 'Optical', batchNo: 'FRM-TX9', expDate: '', stockQty: 12, reorderLevel: 3, avgDailyUse: 0, purchasePrice: 2200, salesPrice: 4500, lastSoldDaysAgo: 120 },
    { name: 'Progressive Lens Pair (1.67 AR)', category: 'Optical', batchNo: 'LNS167', expDate: '', stockQty: 30, reorderLevel: 8, avgDailyUse: 1.2, purchasePrice: 900, salesPrice: 2200, lastSoldDaysAgo: 3 },
    { name: 'Tobramycin+Dexa eye drops', category: 'Pharmacy', batchNo: 'TD09', expDate: '2026-06-15', stockQty: 22, reorderLevel: 10, avgDailyUse: 1.5, purchasePrice: 70, salesPrice: 110, lastSoldDaysAgo: 4 },
    { name: 'Rimless Frame Classic R-2', category: 'Optical', batchNo: 'FRM-R2', expDate: '', stockQty: 8, reorderLevel: 4, avgDailyUse: 0.02, purchasePrice: 1400, salesPrice: 3200, lastSoldDaysAgo: 95 },
  ];

  constructor(private assist: AiAssistService) {}

  ngOnInit(): void {
    this.assist.inventoryBranches().subscribe({
      next: (res) => { this.branches = (res && res.branches) || []; },
      error: () => { this.branches = []; },
    });
    this.run();
  }

  onBranchChange(): void {
    this.run();
  }

  run(): void {
    this.loading = true;
    this.errored = false;
    this.data = null;

    // explicit items provided -> use them
    if (this.items && this.items.length) {
      this.usingSample = false;
      this.analyse(this.assist.inventoryInsights(this.items, this.asOfDate));
      return;
    }

    // otherwise try LIVE database stock first, fall back to sample
    this.assist.inventoryInsightsLive(this.asOfDate, this.selectedBranchId).subscribe({
      next: (res) => {
        if (res && res.ok && (res.itemCount || 0) > 0) {
          this.usingSample = false;
          this.apply(res);
        } else {
          this.usingSample = true;
          this.analyse(this.assist.inventoryInsights(this.sample, this.asOfDate));
        }
      },
      error: () => {
        this.usingSample = true;
        this.analyse(this.assist.inventoryInsights(this.sample, this.asOfDate));
      },
    });
  }

  private analyse(obs: any): void {
    obs.subscribe({
      next: (res: InventoryInsights) => this.apply(res),
      error: () => { this.loading = false; this.errored = true; },
    });
  }

  private apply(res: InventoryInsights): void {
    this.loading = false;
    this.data = res;
    this.errored = !res.ok;
    if (res.stats) { this.animateStats(res.stats); }
    if (res.savings) { this.animateSavings(res.savings); }
  }

  private animateStats(s: any): void {
    this.countTo('cItems', s.totalItems || 0, 500);
    this.countTo('cExpiry', s.expiringSoon || 0, 700);
    this.countTo('cReorder', s.belowReorder || 0, 700);
    this.countTo('cDead', s.deadStock || 0, 700);
    this.countTo('cValue', Math.round(s.atRiskValue || 0), 1100);
  }

  private animateSavings(s: any): void {
    this.cImpact = this.cWriteOff = this.cFree = this.cProtected = 0;
    this.countTo('cWriteOff', Math.round(s.writeOffAvoided || 0), 1300);
    this.countTo('cFree', Math.round(s.capitalToFree || 0), 1300);
    this.countTo('cProtected', Math.round(s.salesProtected || 0), 1300);
    this.countTo('cImpact', Math.round(s.totalImpact || 0), 1600);
    if ((s.totalImpact || 0) > 0) { this.launchConfetti(); }
  }

  private launchConfetti(): void {
    const colors = ['#10b981', '#22d3ee', '#6366f1', '#a855f7', '#f59e0b', '#34d399'];
    const pieces = [];
    for (let i = 0; i < 70; i++) {
      pieces.push({ left: Math.random() * 100, delay: Math.random() * 0.6, bg: colors[i % colors.length], rot: Math.random() * 360 });
    }
    this.confetti = pieces;
    setTimeout(() => (this.confetti = []), 4000);
  }

  private countTo(prop: string, target: number, ms: number): void {
    const steps = 28;
    let i = 0;
    const inc = target / steps;
    const t = setInterval(() => {
      i++;
      (this as any)[prop] = Math.round(inc * i);
      if (i >= steps) { (this as any)[prop] = target; clearInterval(t); }
    }, ms / steps);
  }

  inr(v: number): string {
    return '₹' + (v || 0).toLocaleString('en-IN');
  }
}
